import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { format } from 'date-fns';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import { apiFetch } from '../services/httpClient';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Auth is httpOnly-cookie based (see AuthContext/api.js) — the browser sends
// the session cookie automatically on same-origin requests, and httpClient's
// apiFetch forwards it via credentials: 'include'. No Authorization header
// is needed here; a synthesized "Bearer null" would have made the server
// reject the request even though the valid cookie would otherwise
// authenticate it.

// Fetch notifications
const fetchNotifications = async ({ page, limit, status, type }) => {
  const params = new URLSearchParams({ page, limit });
  if (status) params.append('status', status);
  if (type) params.append('type', type);

  return apiFetch(
    `${API_BASE}/api/notifications?${params}`,
    {},
    { fallbackMessage: 'Failed to fetch notifications', suppressGlobalError: true }
  );
};

// Create notification
const createNotification = async (data) => {
  return apiFetch(
    `${API_BASE}/api/notifications`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    },
    { fallbackMessage: 'Failed to create notification', suppressGlobalError: true }
  );
};

// Send notification
const sendNotification = async (id) => {
  return apiFetch(
    `${API_BASE}/api/notifications/${id}/send`,
    { method: 'POST' },
    { fallbackMessage: 'Failed to send notification', suppressGlobalError: true }
  );
};

// Cancel notification
const cancelNotification = async (id) => {
  return apiFetch(
    `${API_BASE}/api/notifications/${id}/cancel`,
    { method: 'POST' },
    { fallbackMessage: 'Failed to cancel notification', suppressGlobalError: true }
  );
};

const Notifications = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info',
    priority: 'normal',
    targetType: 'all',
    targetTiers: [],
    requiresConfirmation: false,
    sendImmediately: false,
  });

  const { data, isLoading, error, refetch } = useQuery(
    ['notifications', page + 1, rowsPerPage, statusFilter, typeFilter],
    () => fetchNotifications({ page: page + 1, limit: rowsPerPage, status: statusFilter, type: typeFilter }),
    { keepPreviousData: true }
  );

  // Error state for showing feedback
  const [mutationError, setMutationError] = React.useState(null);

  const createMutation = useMutation(createNotification, {
    onSuccess: (data) => {
      // Check if the API returned success: false
      if (data && data.success === false) {
        setMutationError(data.error || 'Failed to create notification');
        return;
      }
      // Success - close dialog and refresh
      setMutationError(null);
      queryClient.invalidateQueries('notifications');
      setCreateDialogOpen(false);
      resetForm();
    },
    onError: (error) => {
      console.error('Failed to create notification:', error);
      setMutationError(error.message || 'Failed to create notification');
    }
  });

  const sendMutation = useMutation(sendNotification, {
    onSuccess: () => {
      setMutationError(null);
      queryClient.invalidateQueries('notifications');
    },
    onError: (error) => {
      console.error('Failed to send notification:', error);
      setMutationError(error.message || 'Failed to send notification');
    }
  });

  const cancelMutation = useMutation(cancelNotification, {
    onSuccess: () => {
      setMutationError(null);
      queryClient.invalidateQueries('notifications');
    },
    onError: (error) => {
      console.error('Failed to cancel notification:', error);
      setMutationError(error.message || 'Failed to cancel notification');
    }
  });

  const resetForm = () => {
    setFormData({
      title: '',
      message: '',
      type: 'info',
      priority: 'normal',
      targetType: 'all',
      targetTiers: [],
      requiresConfirmation: false,
      sendImmediately: false,
    });
  };

  const handleCreateSubmit = () => {
    setMutationError(null); // Clear any previous errors
    createMutation.mutate(formData);
  };

  // Handle dialog close - reset error state
  const handleDialogClose = () => {
    setMutationError(null);
    setCreateDialogOpen(false);
    resetForm();
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      scheduled: 'warning',
      sent: 'success',
      expired: 'error',
      cancelled: 'error'
    };
    return colors[status] || 'default';
  };

  const getTypeColor = (type) => {
    const colors = {
      info: 'info',
      warning: 'warning',
      critical: 'error',
      maintenance: 'secondary',
      feature: 'primary',
      promotion: 'success'
    };
    return colors[type] || 'default';
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <Alert severity="error">Error loading notifications</Alert>;

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button startIcon={<RefreshIcon />} onClick={() => refetch()}>Refresh</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)}>
            Create Notification
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {mutationError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMutationError(null)}>
          {mutationError}
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="scheduled">Scheduled</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
            <InputLabel>Type</InputLabel>
            <Select value={typeFilter} label="Type" onChange={(e) => setTypeFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="info">Info</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
              <MenuItem value="maintenance">Maintenance</MenuItem>
              <MenuItem value="feature">Feature</MenuItem>
              <MenuItem value="promotion">Promotion</MenuItem>
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Notifications Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Target</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Confirmations</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.notifications?.map((notification) => (
              <TableRow key={notification._id}>
                <TableCell>{notification.title}</TableCell>
                <TableCell><Chip size="small" label={notification.type} color={getTypeColor(notification.type)} /></TableCell>
                <TableCell>
                  {notification.targetType === 'all' ? 'All Users' :
                   notification.targetType === 'tier' ? `Tiers: ${notification.targetTiers?.join(', ')}` :
                   notification.targetType === 'company' ? `${notification.targetCompanyIds?.length} companies` :
                   `Business: ${notification.targetBusinessTypes?.join(', ')}`}
                </TableCell>
                <TableCell><Chip size="small" label={notification.status} color={getStatusColor(notification.status)} /></TableCell>
                <TableCell>
                  {notification.requiresConfirmation ?
                    `${notification.stats?.confirmedCount || 0}/${notification.stats?.targetedCount || 0}` :
                    '-'}
                </TableCell>
                <TableCell>{format(new Date(notification.createdAt), 'MMM d, yyyy HH:mm')}</TableCell>
                <TableCell>
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => { setSelectedNotification(notification); setViewDialogOpen(true); }}>
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  {['draft', 'scheduled'].includes(notification.status) && (
                    <>
                      <Tooltip title="Send Now">
                        <IconButton size="small" color="primary" onClick={() => sendMutation.mutate(notification._id)}>
                          <SendIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton size="small" color="error" onClick={() => cancelMutation.mutate(notification._id)}>
                          <CancelIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={data?.pagination?.total || 0}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </TableContainer>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>Create Notification</DialogTitle>
        <DialogContent>
          {/* Error display inside dialog */}
          {mutationError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMutationError(null)}>
              {mutationError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Title" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={4} label="Message" value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select value={formData.type} label="Type" onChange={(e) => setFormData({...formData, type: e.target.value})}>
                  <MenuItem value="info">Info</MenuItem>
                  <MenuItem value="warning">Warning</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                  <MenuItem value="feature">Feature</MenuItem>
                  <MenuItem value="promotion">Promotion</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select value={formData.priority} label="Priority" onChange={(e) => setFormData({...formData, priority: e.target.value})}>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Target</InputLabel>
                <Select value={formData.targetType} label="Target" onChange={(e) => setFormData({...formData, targetType: e.target.value})}>
                  <MenuItem value="all">All Users</MenuItem>
                  <MenuItem value="tier">By Tier</MenuItem>
                  <MenuItem value="business_type">By Business Type</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formData.targetType === 'tier' && (
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Tiers</InputLabel>
                  <Select multiple value={formData.targetTiers} label="Tiers" onChange={(e) => setFormData({...formData, targetTiers: e.target.value})}>
                    <MenuItem value="free">Free</MenuItem>
                    <MenuItem value="professional">Professional</MenuItem>
                    <MenuItem value="enterprise">Enterprise</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={6}>
              <FormControlLabel control={<Switch checked={formData.requiresConfirmation} onChange={(e) => setFormData({...formData, requiresConfirmation: e.target.checked})} />} label="Require Confirmation" />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel control={<Switch checked={formData.sendImmediately} onChange={(e) => setFormData({...formData, sendImmediately: e.target.checked})} />} label="Send Immediately" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={createMutation.isLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateSubmit} disabled={createMutation.isLoading || !formData.title || !formData.message}>
            {createMutation.isLoading ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedNotification?.title}</DialogTitle>
        <DialogContent>
          {selectedNotification && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}><Typography variant="body1">{selectedNotification.message}</Typography></Grid>
              <Grid item xs={6}><Typography variant="caption" color="textSecondary">Type:</Typography><Chip size="small" label={selectedNotification.type} color={getTypeColor(selectedNotification.type)} sx={{ ml: 1 }} /></Grid>
              <Grid item xs={6}><Typography variant="caption" color="textSecondary">Status:</Typography><Chip size="small" label={selectedNotification.status} color={getStatusColor(selectedNotification.status)} sx={{ ml: 1 }} /></Grid>
              <Grid item xs={6}><Typography variant="caption" color="textSecondary">Created:</Typography><Typography variant="body2" sx={{ ml: 1 }}>{format(new Date(selectedNotification.createdAt), 'PPpp')}</Typography></Grid>
              {selectedNotification.sentAt && <Grid item xs={6}><Typography variant="caption" color="textSecondary">Sent:</Typography><Typography variant="body2" sx={{ ml: 1 }}>{format(new Date(selectedNotification.sentAt), 'PPpp')}</Typography></Grid>}
              {selectedNotification.requiresConfirmation && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Confirmations: {selectedNotification.stats?.confirmedCount || 0} / {selectedNotification.stats?.targetedCount || 0}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setViewDialogOpen(false)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default Notifications;

